import React, { useState } from 'react';
import {
  DatePicker,
  Table,
  Card,
  Spin,
  message,
  Input,
  Button,
  Space,
} from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import dailyEmployeeFundService, {
  DailyEmployeeFundSummary,
} from '../../service/report-service/DailyEmployeeFund.service';
import { formatCurrency } from '../../utils/format';

// Hàm filter search cho text (tên nhân viên)
const getColumnSearchProps = (
  dataIndex: string
): ColumnType<DailyEmployeeFundSummary> => ({
  filterDropdown: ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }: FilterDropdownProps) => (
    <div style={{ padding: 8 }}>
      <Input
        placeholder={`Tìm kiếm`}
        value={selectedKeys[0]}
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() => confirm()}
        style={{ marginBottom: 8, display: 'block' }}
      />
      <Space>
        <Button
          type="primary"
          onClick={() => confirm()}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Tìm kiếm
        </Button>
        <Button
          onClick={() => clearFilters && clearFilters()}
          size="small"
          style={{ width: 90 }}
        >
          Xóa
        </Button>
      </Space>
    </div>
  ),
  filterIcon: (filtered: boolean) => (
    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) =>
    record.employee?.name
      ?.toString()
      .toLowerCase()
      .includes((value as string).toLowerCase()),
});

// Hàm filter cho cột số (min-max)
const getNumberColumnFilterProps = (
  dataIndex: keyof DailyEmployeeFundSummary | 'employeeId'
): ColumnType<DailyEmployeeFundSummary> => ({
  filterDropdown: ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }: FilterDropdownProps) => {
    // selectedKeys[0] sẽ là [min, max] dạng tuple string
    const [min, max] = (selectedKeys[0] as unknown as [string, string]) || [
      '',
      '',
    ];
    return (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Từ"
          type="number"
          value={min}
          onChange={(e) => {
            const val = e.target.value;
            setSelectedKeys([[val, max] as unknown as React.Key]);
          }}
          style={{ width: 80, marginBottom: 8, display: 'inline-block' }}
        />
        <span style={{ margin: '0 8px' }}>-</span>
        <Input
          placeholder="Đến"
          type="number"
          value={max}
          onChange={(e) => {
            const val = e.target.value;
            setSelectedKeys([[min, val] as unknown as React.Key]);
          }}
          style={{ width: 80, marginBottom: 8, display: 'inline-block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90 }}
          >
            Lọc
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </Space>
      </div>
    );
  },
  filterIcon: (filtered: boolean) => (
    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    const [min, max] = (value as unknown as [string, string]) || ['', ''];
    let num: number = 0;
    if (dataIndex === 'employeeId') {
      num = record.employee?.id ?? 0;
    } else {
      num = Number(record[dataIndex as keyof DailyEmployeeFundSummary]) || 0;
    }
    if (min && max) return num >= Number(min) && num <= Number(max);
    if (min) return num >= Number(min);
    if (max) return num <= Number(max);
    return true;
  },
});

const EmployeeCheck: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DailyEmployeeFundSummary[]>([]);

  const columns: ColumnsType<DailyEmployeeFundSummary> = [
    {
      title: 'Thông tin nhân viên',
      dataIndex: ['employee', 'name'],
      key: 'employeeName',
      fixed: 'left',
      width: 200,
      ...getColumnSearchProps('employee.name'),
    },
    {
      title: 'Tổng nạp đáo',
      dataIndex: 'totalDeposits',
      key: 'totalDeposits',
      align: 'right',
      width: 150,
      render: (value: number) => formatCurrency(value),
      ...getNumberColumnFilterProps('totalDeposits'),
    },
    {
      title: 'Tổng rút',
      dataIndex: 'totalWithdrawals',
      key: 'totalWithdrawals',
      align: 'right',
      width: 150,
      render: (value: number) => formatCurrency(value),
      ...getNumberColumnFilterProps('totalWithdrawals'),
    },
    {
      title: 'Quỹ đầu ngày',
      dataIndex: 'startingBalance',
      key: 'startingBalance',
      align: 'right',
      width: 150,
      render: (value: number) => formatCurrency(value),
      ...getNumberColumnFilterProps('startingBalance'),
    },
    {
      title: 'Quỹ đầu ngày + Phí thu KH',
      dataIndex: 'balanceWithFees',
      key: 'balanceWithFees',
      align: 'right',
      width: 200,
      render: (value: number) => formatCurrency(value),
      ...getNumberColumnFilterProps('balanceWithFees'),
    },
    {
      title: 'Quỹ đầu ngày - Nạp đáo + Phí thu KH',
      dataIndex: 'finalBalance',
      key: 'finalBalance',
      align: 'right',
      width: 250,
      render: (value: number) => formatCurrency(value),
      ...getNumberColumnFilterProps('finalBalance'),
    },
  ];

  const fetchData = async (date: dayjs.Dayjs) => {
    try {
      setLoading(true);
      const formattedDate = date.format('YYYY-MM-DD');
      const response =
        await dailyEmployeeFundService.getAllDailyFundsByDate(formattedDate);
      // Sắp xếp theo employee.id từ bé đến lớn
      response.sort((a, b) => (a.employee.id ?? 0) - (b.employee.id ?? 0));
      setData(response);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setSelectedDate(date);
      fetchData(date);
    }
  };

  // Fetch data on component mount
  React.useEffect(() => {
    fetchData(selectedDate);
  }, []);

  return (
    <Card title="Kiểm tra quỹ nhân viên" className="m-4">
      <div className="mb-4">
        <DatePicker
          value={selectedDate}
          onChange={handleDateChange}
          format="DD/MM/YYYY"
          className="w-40"
        />
      </div>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          scroll={{ x: 1100 }}
          sticky={true}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} bản ghi`,
          }}
          bordered
        />
      </Spin>
    </Card>
  );
};

export default EmployeeCheck;
